import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import React, {useRef, useState} from "react";
import {toast} from "react-toastify";
import styled from "styled-components";
import {messageApi} from "../../api/message-api";
import {Confirm} from "../../types/confirm";
import {PushMessagePayload, PushMessageReceiverPayload} from "../../types/message/message-payload";
import {validateNotificationInput} from "../../utils/validator/notifiaction";
import {ContainedYellowButton} from "../button/ContainedYellowButton";
import {GeneralTextField} from "../text-field/GeneralTextField";
import {useAppSelector} from "../../app/hooks";
import {profileState} from "../../app/reducers/profile-slice";
import theme from "../../style/theme";
import {Selector} from "../selector/Selector";
import {useCompanyWithCollaborativesPageAllQuery} from "../../query/company-query";
import {isSuperuser} from "../../utils/user/check-user-type";
import {companyState} from "../../app/reducers/company-slice";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";

const initPushMessageReceiverPayload: PushMessageReceiverPayload = {
    companyIds: undefined,
}

const initPushMessagePayload: PushMessagePayload = {
    title: "",
    body: "",
    receiver: initPushMessageReceiverPayload,
};

const initPushMessagePayloadValidator = {
    messageTitle: false,
    messageBody: false,
};

const placeholder = {
    messageTitle: "제목을 입력해주세요",
    messageBody: "내용을 입력해주세요",
};

export const NotificationForm = () => {
    const [pushMessagePayload, setPushMessagePayload] =
        useState<PushMessagePayload>(initPushMessagePayload);
    const [pushMessagePayloadValidator, setPushMessagePayloadValidator] =
        useState(initPushMessagePayloadValidator);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const {profile} = useAppSelector(profileState);

    const {companyWithCollaborativesPageAll} = useAppSelector(companyState);
    useCompanyWithCollaborativesPageAllQuery(isSuperuser(profile));

    const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

    const [companyMap, setCompanyMap] = useState<{
        [key: string]: string;
    }>({});

    const handleClickCompany = async (key: string) => {
        setSelectedCompanies((prevStatus) => {
            if (prevStatus.includes(key)) {
                return prevStatus.filter((v) => v !== key);
            } else {
                return [...prevStatus, key];
            }
        });
    };

    useDidMountEffect(() => {
        setCompanies();
    }, [companyWithCollaborativesPageAll]);


    const setCompanies = () => {
        const map: { [key: string]: string } = {};
        companyWithCollaborativesPageAll.contents.map((c) => {
            map[c.id] = c.name
        });
        setCompanyMap(map);
    };

    const handleChangeTextField = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setPushMessagePayload({
            ...pushMessagePayload,
            [event.target.name]: event.target.value,
        });
    };

    const handleChangeTextArea = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        setPushMessagePayload({
            ...pushMessagePayload,
            [event.target.name]: event.target.value,
        });
    };

    useDidMountEffect(() => {
        setPushMessagePayload({
            ...pushMessagePayload,
            receiver: {
                companyIds: selectedCompanies.join(";")
            }
        });
    }, [selectedCompanies]);

    const handleClickSubmitButton = async () => {
        const {hasError, validationResults} =
            validateNotificationInput(pushMessagePayload);
        if (hasError) {
            setPushMessagePayloadValidator(validationResults);
            toast.error(Confirm.Error);
            return;
        }

        let payload;
        let confirmMessage;
        if (isSuperuser(profile) && selectedCompanies.length == 0) {
            payload = {
                ...pushMessagePayload,
                receiver: {
                    companyIds: undefined,
                }
            };
            confirmMessage = "알림이 앱을 사용하는 모든 배송원에게 전송됩니다. 전송하시겠습니까?";
        } else {
            payload = {
                ...pushMessagePayload,
                receiver: {
                    companyIds: profile?.company.id ?? undefined,
                }
            };
            confirmMessage = `알림이 앱을 사용하는 ${profile?.company.name ?? '고객사'} 배송원에게 모두 전송됩니다. 알림을 전송하시겠습니까?`;
        }

        try {
            if (window.confirm(confirmMessage)) {
                await messageApi.postPush(payload);
                toast.success(Confirm.PostNotification);
            }
        } catch (error) {
            console.log("postPush error", error);
        }
        setPushMessagePayloadValidator(initPushMessagePayloadValidator);
    };

    return (
        <NotificationFormContainer>
            <NotificationFormTitleContainer>
                <NotificationsActiveIcon
                    style={{
                        marginRight: "6px",
                        width: "20px",
                        height: "20px",
                        color: "#F14800"
                    }}
                />
                <NotificationFormTitleLabel>앱 푸시알림 전송하기</NotificationFormTitleLabel>
            </NotificationFormTitleContainer>

            <FlexRawContainer>
                <InputTitleLabel>알림 제목</InputTitleLabel>
                <GeneralTextField
                    placeholder={placeholder.messageTitle}
                    name={"title"}
                    value={pushMessagePayload.title}
                    onChange={handleChangeTextField}
                    width="24.125rem"
                    isError={pushMessagePayloadValidator.messageTitle}
                />
            </FlexRawContainer>

            <FlexRawContainer>
                <InputTitleLabel>알림 내용</InputTitleLabel>
                <TextAreaBox>
                    <CustomTextArea
                        ref={textAreaRef}
                        name={"body"}
                        onChange={handleChangeTextArea}
                        placeholder={placeholder.messageBody}
                        defaultValue={pushMessagePayload.body}
                    ></CustomTextArea>
                </TextAreaBox>
            </FlexRawContainer>

            <FlexRawContainer>
                <InputTitleLabel>대상</InputTitleLabel>
                {profile?.company.name === "SUPERUSER" ? (
                    <NotificationReceiveClientContainer>
                        <Selector
                            keys={selectedCompanies}
                            defaultKey={""}
                            candidateMap={companyMap}
                            onClickFilterMultiSelectItem={handleClickCompany}
                            width="24.125rem"
                            placeholder="고객사"
                        />
                        <NotificationReceiverLabel>
                            *선택된 고객사 배송원 전체에게 푸시 알림 메시지를 전송합니다.
                            <br/>
                            *선택하지 않을 경우 고객사 배송원 포함 전체 배송원에게 전송합니다.
                        </NotificationReceiverLabel>
                    </NotificationReceiveClientContainer>
                ) : (
                    <NotificationReceiverLabel>
                        *{profile?.company.name ?? ""} 소속 배송원 전체에게 푸시 알림 메시지를 전송합니다.
                    </NotificationReceiverLabel>
                )}
            </FlexRawContainer>

            <ButtonContainer>
                <ContainedYellowButton
                    width="8.188rem"
                    height="3.188rem"
                    text={"전송하기"}
                    onClick={handleClickSubmitButton}
                />
            </ButtonContainer>
        </NotificationFormContainer>
    );
};

const NotificationFormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const NotificationFormTitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 28px 0;
`;

const NotificationFormTitleLabel = styled.div`
  margin-top: 2px;
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.375rem;
  color: rgb(0, 0, 0);
`;

const InputTitleLabel = styled.div`
  font-weight: 500;
  font-size: 0.813rem;
  line-height: 1rem;
  color: rgb(77, 83, 92);
  margin-bottom: 4px;
`;

const FlexRawContainer = styled.div`
  margin: 10px 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 32px;
`;

const NotificationReceiveClientContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const NotificationReceiverLabel = styled.p`
  font-weight: 400;
  font-size: 13px;
  margin: 6px 0;
  color: ${theme.palette.primitives.gray400};
  line-height: 15px;
`

const TextAreaBox = styled.div`
  width: 100%;
  height: 160px;
`;

const CustomTextArea = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 11px;
  font-size: 14px;
  font-weight: 400;
  font-family: Pretendard;
  border-radius: 4px;
  line-height: 19px;
  color: ${theme.palette.primitives.gray900};
  overflow: auto;
  resize: none;
  box-sizing: border-box;
  outline: none;
  background: transparent;
  border: 1px solid ${theme.palette.primitives.gray300};

  &::placeholder {
    color: ${theme.palette.primitives.gray300}
  }

  &:focus {
    border-color: #fcca00;
    outline: none;
  }
`;
